import type { ScheduledJob } from "./scheduler-backend";
import { triggerGeneration, type GenerationDeps } from "./scheduler-generation";
import type { ResolvedSchedulerConfig } from "./types/scheduler";

export interface GenerationScheduleRunnerDeps {
  config: ResolvedSchedulerConfig;
  getGenerationDeps: () => GenerationDeps;
  isRunning: () => boolean;
}

/**
 * Handles scheduled draft generation triggers.
 * ContentScheduler owns the public API and delegates generation jobs here.
 */
export class GenerationScheduleRunner {
  private readonly deps: GenerationScheduleRunnerDeps;
  private generationJobs: Map<string, ScheduledJob> = new Map();

  constructor(deps: GenerationScheduleRunnerDeps) {
    this.deps = deps;
  }

  public start(): void {
    for (const [entityType, cronExpr] of Object.entries(
      this.generationSchedules,
    )) {
      const job = this.deps.config.backend.scheduleCron(cronExpr, () =>
        this.handleTriggerGeneration(entityType),
      );
      this.generationJobs.set(entityType, job);
    }
  }

  public async stop(): Promise<void> {
    const jobs = Array.from(this.generationJobs.values());
    this.generationJobs.clear();
    await Promise.all(jobs.map((job) => job.stop()));
  }

  private async handleTriggerGeneration(entityType: string): Promise<void> {
    if (!this.deps.isRunning()) return;

    try {
      await triggerGeneration(entityType, this.deps.getGenerationDeps());
    } catch (error) {
      this.deps.config.logger.error(
        `Generation trigger error for ${entityType}:`,
        error,
      );
    }
  }

  private get generationSchedules(): Record<string, string> {
    return this.deps.config.generationSchedules;
  }
}
